import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import {
  AppNotification,
  BellFeedResponse,
  NotificationListQuery,
  NotificationListResponse,
  NotificationPreference,
  UpdateNotificationPreferencesRequest,
} from '../models/notification.model';

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly http = inject(HttpClient);
  private readonly bellUrl = '/api/notifications/bell';
  private readonly prefsUrl = '/api/notification-preferences';

  private readonly _notifications = signal<AppNotification[]>([]);

  readonly undismissedNotifications = computed(() =>
    this._notifications().filter(n => !n.isDismissed),
  );

  readonly hasUndismissed = computed(() =>
    this.undismissedNotifications().length > 0,
  );

  loadBellFeed(): void {
    this.http.get<BellFeedResponse>(this.bellUrl).pipe(take(1)).subscribe({
      next: (res) => this._notifications.set(res.items),
      error: (err) => console.error('Failed to load notifications', err),
    });
  }

  dismiss(notificationId: string): void {
    this._notifications.update(ns =>
      ns.map(n => n.notificationId === notificationId ? { ...n, isDismissed: true } : n),
    );
    this.http.post<void>(`/api/notifications/${notificationId}/dismiss`, {}).pipe(take(1)).subscribe({
      error: (err) => {
        console.error('Failed to dismiss notification', err);
        this._notifications.update(ns =>
          ns.map(n => n.notificationId === notificationId ? { ...n, isDismissed: false } : n),
        );
      },
    });
  }

  dismissAll(): void {
    const previous = this._notifications();
    this._notifications.update(ns => ns.map(n => ({ ...n, isDismissed: true })));
    this.http.post<void>('/api/notifications/dismiss-all', {}).pipe(take(1)).subscribe({
      error: (err) => {
        console.error('Failed to dismiss all notifications', err);
        this._notifications.set(previous);
      },
    });
  }

  getList(query: NotificationListQuery = {}): Observable<NotificationListResponse> {
    let params = new HttpParams()
      .set('page', String(query.page ?? 1))
      .set('pageSize', String(query.pageSize ?? 10));
    if (query.module) params = params.set('module', query.module);
    if (query.search) params = params.set('search', query.search);
    if (query.dismissed !== undefined) params = params.set('dismissed', String(query.dismissed));
    if (query.type) params = params.set('type', query.type);
    return this.http.get<NotificationListResponse>('/api/notifications', { params });
  }

  getPreferences(): Observable<NotificationPreference> {
    return this.http.get<NotificationPreference>(this.prefsUrl);
  }

  savePreferences(request: UpdateNotificationPreferencesRequest): Observable<void> {
    return this.http.put<void>(this.prefsUrl, request);
  }
}
